import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  useTheme,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import WorkIcon from '@mui/icons-material/Work';
import BadgeIcon from '@mui/icons-material/Badge';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

interface Employee {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
  sales_count: number;
  expenses_count: number;
  full_name?: string;
  avatar_url?: string;
  status?: string;
  position?: string;
}

export function Employees() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [handlerCreds, setHandlerCreds] = useState<{email: string, password: string} | null>(null);
  
  // Create Handler State
  const [createOpen, setCreateOpen] = useState(false);
  const [newHandlerName, setNewHandlerName] = useState('');
  const [newHandlerEmail, setNewHandlerEmail] = useState('');
  const [newHandlerPassword, setNewHandlerPassword] = useState('');
  const [creatingHandler, setCreatingHandler] = useState(false);
  const [createError, setCreateError] = useState('');
  
  const theme = useTheme();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      const { data: userRoles, error: authError } = await supabase
        .from('user_roles')
        .select(`
          role,
          user_id,
          users (
            id,
            email,
            full_name,
            avatar_url,
            created_at,
            last_sign_in_at
          )
        `);
        
      if (authError) throw authError;
      
      if (!userRoles) {
        setEmployees([]);
        return;
      }
      
      const { data: salesData } = await supabase
        .from('sales')
        .select('employee_id');
      
      const { data: expensesData } = await supabase
        .from('expenses')
        .select('employee_id');
        
      const employeesWithStats = userRoles.map((ur: any) => {
        // Handle case where users relation is null (shouldn't happen with proper foreign key)
        const user = ur.users || { 
          id: ur.user_id, 
          email: 'unknown', 
          created_at: new Date().toISOString() 
        };
        
        const salesCount = salesData?.filter(s => s.employee_id === user.id).length || 0;
        const expensesCount = expensesData?.filter(e => e.employee_id === user.id).length || 0;
        
        return {
          ...user,
          role: ur.role,
          sales_count: salesCount,
          expenses_count: expensesCount,
          full_name: user.full_name || user.email.split('@')[0],
          status: user.last_sign_in_at ? 'Active' : 'Inactive',
          position: ur.role === 'owner' ? 'Owner' : 'Employee',
        };
      });
      
      setEmployees(employeesWithStats);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (employee: Employee) => {
    setSelectedEmployee(employee);
    setHandlerCreds(null);
    setDetailsOpen(true);
    
    // Fetch credentials if it's a handler
    if (employee.role !== 'owner') {
      try {
        const { data, error } = await supabase
          .from('handler_credentials')
          .select('email, password')
          .eq('user_id', employee.id)
          .single();
          
        if (!error && data) {
          setHandlerCreds(data);
        }
      } catch (err) {
        console.error('Error fetching credentials', err);
      }
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedEmployee(null);
    setHandlerCreds(null);
  };
  
  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';
    let pass = '';
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewHandlerPassword(pass);
  };
  
  const handleCreateHandler = async () => {
    if (!newHandlerEmail || !newHandlerPassword || !newHandlerName) {
      setCreateError('All fields are required');
      return;
    }
    
    setCreatingHandler(true);
    setCreateError('');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/create-handler', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          email: newHandlerEmail,
          password: newHandlerPassword,
          fullName: newHandlerName
        })
      });
      
      const isHtml = response.headers.get('content-type')?.includes('text/html');
      if (isHtml) {
        throw new Error('Handler creation requires Vercel CLI locally. Please run with `vercel dev` or test on the deployed app.');
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create handler');
      }
      
      // Success
      setCreateOpen(false);
      setNewHandlerEmail('');
      setNewHandlerPassword('');
      setNewHandlerName('');
      fetchEmployees(); // Refresh list
    } catch (err: any) {
      setCreateError(err.message);
    } finally {
      setCreatingHandler(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    
    if (searchTerm) {
      filtered = filtered.filter(employee => 
        (employee.email && employee.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (employee.full_name && employee.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (employee.role && employee.role.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(employee => employee.role === roleFilter);
    }
    
    return filtered;
  }, [employees, searchTerm, roleFilter]);

  const columns: GridColDef[] = [
    { 
      field: 'full_name', 
      headerName: 'Name', 
      width: 200,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            src={params.row.avatar_url || ''}
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: theme.palette.primary.main,
              mr: 1,
              fontSize: '0.9rem'
            }}
          >
            {params.row.full_name?.charAt(0).toUpperCase() || params.row.email.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={500} noWrap>
            {params.row.full_name || params.row.email.split('@')[0]}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 230,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', opacity: 0.7 }} />
          <Typography variant="body2" noWrap>
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Chip 
          label={params.row.role === 'owner' ? 'Owner' : 'Employee'} 
          size="small" 
          color={params.row.role === 'owner' ? 'secondary' : 'primary'} 
          variant="outlined" 
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 150,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', opacity: 0.7 }} />
          <Typography variant="body2">
            {format(new Date(params.row.created_at), 'MMM dd, yyyy')}
          </Typography>
        </Box>
      ),
      valueGetter: (params: GridValueGetterParams<Employee>) => 
        new Date(params.row.created_at).toISOString(),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Chip 
          label={params.row.status} 
          size="small" 
          color={params.row.status === 'Active' ? 'success' : 'default'} 
          variant={params.row.status === 'Active' ? 'filled' : 'outlined'} 
        />
      ),
    },
    {
      field: 'sales_count',
      headerName: 'Sales',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams<Employee>) => 
        params.row.sales_count || 0,
    },
    {
      field: 'expenses_count',
      headerName: 'Expenses',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams<Employee>) => 
        params.row.expenses_count || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Tooltip title="View Details & Credentials">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box className="fade-in" sx={{ pt: 1 }}>
      <Card 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          mb: 3
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={600}>
              Employees & Team Members
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Manage your team and generate handler accounts
            </Typography>
          }
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => setCreateOpen(true)}
            >
              Generate Handler
            </Button>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mb: 2 }}>
                  <BadgeIcon fontSize="large" />
                </Avatar>
                <Typography variant="h5" fontWeight={600}>
                  {employees.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Team Members
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56, mb: 2 }}>
                  <WorkIcon fontSize="large" />
                </Avatar>
                <Typography variant="h5" fontWeight={600}>
                  {employees.filter(e => e.status === 'Active').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Employees
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56, mb: 2 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Typography variant="h5" fontWeight={600}>
                  {employees.filter(e => e.role === 'owner').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Owners / Admins
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'visible'
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={600}>
              Employee Directory
            </Typography>
          }
          sx={{ pb: 0 }}
        />
        
        <CardContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search employees..." 
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="role-filter-label">Role</InputLabel>
              <Select
                labelId="role-filter-label"
                value={roleFilter}
                label="Role"
                onChange={(e) => setRoleFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="owner">Owners</MenuItem>
                <MenuItem value="employee">Handlers</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6, 
              px: 2, 
              bgcolor: 'background.default',
              borderRadius: 1
            }}>
              <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>No Employees Found</Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || roleFilter !== 'all'
                  ? "No employees match your search criteria. Try different keywords or clear the filters." 
                  : "No employees have been added yet."}
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={filteredEmployees}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: {
                  sortModel: [{ field: 'created_at', sort: 'desc' }],
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              autoHeight
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                },
                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)',
                },
                border: 'none',
                minHeight: 400
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Employee Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        {selectedEmployee && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  src={selectedEmployee.avatar_url || ''}
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    bgcolor: theme.palette.primary.main,
                    mr: 2
                  }}
                >
                  {selectedEmployee.full_name?.charAt(0).toUpperCase() || 
                   selectedEmployee.email.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedEmployee.full_name || selectedEmployee.email.split('@')[0]}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedEmployee.email}</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Role</Typography>
                  <Typography variant="body1">{selectedEmployee.role === 'owner' ? 'Owner' : 'Handler'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status</Typography>
                  <Chip 
                    label={selectedEmployee.status} 
                    size="small" 
                    color={selectedEmployee.status === 'Active' ? 'success' : 'default'} 
                    variant={selectedEmployee.status === 'Active' ? 'filled' : 'outlined'} 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Joined Date</Typography>
                  <Typography variant="body1">{format(new Date(selectedEmployee.created_at), 'MMM dd, yyyy')}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Sign In</Typography>
                  <Typography variant="body1">
                    {selectedEmployee.last_sign_in_at 
                      ? format(new Date(selectedEmployee.last_sign_in_at), 'MMM dd, yyyy HH:mm')
                      : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sales Recorded</Typography>
                  <Typography variant="body1">{selectedEmployee.sales_count || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Expenses Recorded</Typography>
                  <Typography variant="body1">{selectedEmployee.expenses_count || 0}</Typography>
                </Grid>
                
                {handlerCreds && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockOutlinedIcon fontSize="small" />
                        Stored Handler Credentials
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                        Email: {handlerCreds.email}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        Password: {handlerCreds.password}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Share these credentials with the handler so they can log in.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Create Handler Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creatingHandler && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Handler Account</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new handler account. The credentials will be saved so you can retrieve them later if the handler forgets them.
          </Typography>
          
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={newHandlerName}
            onChange={e => setNewHandlerName(e.target.value)}
            disabled={creatingHandler}
          />
          
          <TextField
            label="Email Address"
            fullWidth
            margin="normal"
            value={newHandlerEmail}
            onChange={e => setNewHandlerEmail(e.target.value)}
            disabled={creatingHandler}
          />
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2, mb: 1 }}>
            <TextField
              label="Password"
              fullWidth
              value={newHandlerPassword}
              onChange={e => setNewHandlerPassword(e.target.value)}
              disabled={creatingHandler}
            />
            <Tooltip title="Generate Random Password">
              <IconButton 
                onClick={generateRandomPassword} 
                disabled={creatingHandler}
                sx={{ mt: 1, bgcolor: theme.palette.action.hover }}
              >
                <AutorenewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creatingHandler}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateHandler} 
            disabled={creatingHandler || !newHandlerName || !newHandlerEmail || !newHandlerPassword}
          >
            {creatingHandler ? 'Creating...' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
